import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import type { Session } from "../api";

/**
 * The frame every screen sits in.
 *
 * The navigation comes from the server, which returns only what this
 * principal may open. The client never decides that — it draws what it is
 * given, and every endpoint refuses on its own regardless.
 */
export function Shell({
  session, onSignOut, children,
}: {
  session: Session;
  onSignOut: () => void;
  children: React.ReactNode;
}) {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  let seamDrawn = false;

  // Close the menu when the route changes: on a phone the tap that picked
  // a screen should also put the menu away.
  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <>
      <header className={`top${open ? " top--open" : ""}`}>
        <NavLink className="brand" to="/radar">{session.product ?? "Neev"}</NavLink>
        <button className="btn small navtoggle" aria-expanded={open}
          aria-controls="primary-nav" onClick={() => setOpen((o) => !o)}>
          {open ? "Close" : "Menu"}
        </button>
        <nav className="tabs" id="primary-nav">
          {session.nav.map((item) => {
            const seam = item.group === "reference" && !seamDrawn;
            if (seam) seamDrawn = true;
            const active = pathname === item.href || pathname.startsWith(item.href + "/");
            return (
              <span key={item.href} style={{ display: "contents" }}>
                {seam && <span className="navseam" aria-hidden="true" />}
                <NavLink to={item.href} className={`${item.group}${active ? " on" : ""}`}>
                  {item.label}
                </NavLink>
              </span>
            );
          })}
        </nav>
        <div className="who">
          <span>{session.display_name}</span>
          <span className="roles">{session.roles.join(", ")}</span>
          <button className="btn small" onClick={onSignOut}>Sign out</button>
        </div>
      </header>
      <main>{children}</main>
    </>
  );
}

export function Loading() {
  return <p className="loading">Loading…</p>;
}

/**
 * A refusal, said the way the server said it.
 *
 * The server's message names the role that would have sufficed, or what
 * about the current state stops the action. Replacing it with "Access
 * denied" would throw away the only useful part.
 */
export function Refused({ status, detail }: { status: number; detail: string }) {
  const headings: Record<number, [string, string]> = {
    403: ["Not permitted", "You are signed in, but this needs a role you do not hold."],
    404: ["Not found", "There is nothing here to act on."],
    409: ["Cannot do that yet", "You have the right role. Something about the current state stops this."],
    503: ["Not available", "This depends on something that is not switched on."],
  };
  const [title, standfirst] = headings[status] ?? ["Refused", "That request could not be completed."];
  return (
    <>
      <h1>{title}</h1>
      <p className="standfirst">{standfirst}</p>
      <div className="note red"><span className="h">Why</span>{detail}</div>
    </>
  );
}
